/**
 * Real-runtime test booter.
 *
 * Boots the REAL sandbox runtime — real `ClientProcess`/`ServerProcess` + the real
 * `MockEnvironment` transport (with simulated `simulatedPing` latency) — for a
 * `defineSandboxTest`. It mounts the one sandbox topology (`sandboxPlaygroundDefinition`)
 * through the playground (`mountPlayground`), exactly as the node runner's
 * `bootGeneratedGameNode` does. The real runtime is async + timer-driven, so it only
 * stays deterministic + fast because the caller installs the VIRTUAL CLOCK first
 * (`installVirtualClock`): every `setTimeout`/transport-latency timer + the rAF game
 * loop becomes a virtual timer that fires only when we `advance()` the clock.
 *
 * Produces the SAME `BootedGame` the node runner does, so `runSandboxTest` drives
 * either unchanged.
 */
import type { BootedGame, LoweredSandboxTest, PlaygroundBootedGame, SandboxEnvironment } from '@gotti/sandbox-test';
import {
  advance, settleMicrotasks, virtualNow, pendingTimerCount, getRealSetTimeout, resetVirtualClock, currentVirtualClock,
  applySandboxLatency, disposeSandboxMount, runSandboxMountTest, sandboxClientApi, sandboxClientId, sandboxPlaygroundDefinition,
  sandboxServerApi,
} from '@gotti/sandbox-test';
import type { VirtualClockToken } from '@gotti/sandbox-test';
import { resetOrderedMessageQueues } from '@gotti/test-utils';
import { inputTokenOf, mountPlayground } from '@gotti/playground/browser';
import { focusTestClient, forgetTestClient, setTestClientTokens } from './testInputFocus';

/**
 * The game the test runner has mounted. The page holds one playground mount at a time, and a test the engine abandons
 * at its timeout never reaches its own `destroy` (the step it is stuck in never settles) — so the next boot disposes
 * what is still mounted before it mounts, and the tests after an abandoned one boot as they always did.
 */
let liveMount: { dispose(): Promise<void> } | null = null;

/** Yield a REAL macrotask so genuine browser async (asset image decode/load in
 *  AssetManager.__init) can progress during boot. Boot is one-time setup, NOT the
 *  asserted simulation, so real async here doesn't affect determinism — the
 *  simulation ticks (after boot) use only the virtual clock. */
function realMacrotaskYield(): Promise<void> {
  const realSetTimeout = getRealSetTimeout();
  return new Promise<void>((resolve) => realSetTimeout(() => resolve(), 0));
}
import { getBootAssetSeam, loadSystemsAndExports, setLastBootAssetManager } from './SandboxRuntime';

export interface RealRuntimeSurface {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D | null;
  container: HTMLElement;
}

/** One advance step while pumping the boot — 50ms = one ping-latency window. */
const PUMP_STEP_MS = 50;
/** Max virtual ms to pump a single boot/join before giving up (each join is a few
 *  round-trips, so ~10s of virtual time is ample headroom). */
const PUMP_BUDGET_MS = 12_000;

/** Drive the virtual clock forward until the async `p` (a join round-trip) settles.
 *  Each advance fires the transport's latency timers + drains microtasks so the
 *  join's promise chain progresses; the real per-test timeout (`withTimeout`, on
 *  the REAL clock) still bounds a genuine hang. */
async function pumpUntil<T>(p: Promise<T>, clock?: VirtualClockToken): Promise<T> {
  let settled = false;
  let value: T;
  let err: unknown;
  let failed = false;
  p.then((v) => { settled = true; value = v; }, (e) => { settled = true; failed = true; err = e; });
  // Drive the boot to completion. Each iteration: drain microtasks, yield a REAL
  // macrotask (so real browser async like asset image-decode progresses), and
  // advance virtual time ONLY when there are pending game timers AND we're within
  // the virtual budget. NOT advancing during pure-async phases (asset load,
  // pending=0) keeps virtual time near 0 until the game loops start, so the server
  // loop doesn't boot "behind" and burn a catch-up.
  let iterations = 0;
  const MAX_ITERATIONS = 6000; // real-iteration safety cap (~1ms real each)
  while (!settled && iterations < MAX_ITERATIONS) {
    await settleMicrotasks();
    await realMacrotaskYield();
    if (pendingTimerCount() > 0 && virtualNow() < PUMP_BUDGET_MS) {
      await advance(PUMP_STEP_MS, clock);
    }
    iterations += 1;
  }
  if (!settled) throw new Error(`bootRealRuntime: boot/join did not settle (iters=${iterations}, virtualNow=${virtualNow()}, pending=${pendingTimerCount()})`);
  if (failed) throw err;
  return value!;
}

/**
 * Boot the real runtime for `clientCount` clients, painting each client onto the
 * matching `surfaces[i]` canvas (the same instrumented canvas the live preview
 * shows + the CanvasProbe reads). The virtual clock MUST already be installed.
 */
export async function bootRealRuntime(
  clientCount: number,
  surfaces: RealRuntimeSurface[],
  virtualFiles: Map<string, string>,
  /** The test runner's module context (external modules + the cache the spec was
   *  compiled with). Forwarded to the runtime so its systems and the spec share
   *  ONE instance of every `@/shared/*` class — without this, a component the spec
   *  attaches is a DIFFERENT class identity than the one a server system retrieves
   *  via `getComponent(...)`, so authoritative movement silently no-ops. */
  shared?: { externalModules?: Map<string, unknown>; moduleCache?: Map<string, unknown> },
  /** The test's `latency` (ms one-way client↔server). Default 0 = instant,
   *  deterministic delivery for behavioral tests; a netcode/lag-comp test sets a
   *  realistic value. Applied to the transport config + every client's per-client
   *  simulated latency. The real transport STILL runs — this only sets its delay. */
  latencyMs: number = 0,
  /** The `defineSandboxTest` test this boot is for, lowered (`runSandboxTest` hands it to the booter): the mount carries
   *  it, and the game's `runTest` runs it there — as the node runner's boot does. */
  test?: LoweredSandboxTest,
): Promise<BootedGame> {
  const n = Math.max(1, clientCount);

  // A test abandoned at its timeout left its world mounted: end it before anything of this boot's is set up.
  if (liveMount) {
    const stale = liveMount;
    liveMount = null;
    await disposeSandboxMount(stale);
  }

  // Fresh clock per test boot: drop any timers a previous test's game loops left
  // queued + reset virtual time to 0, so this boot is independent + deterministic.
  resetVirtualClock();
  // The install this game runs on — its run's. Every advance below names it: should a later run supersede it (this
  // run's test timed out while its game went on), this game's ticks are refused instead of driving that run's clock.
  const clock = currentVirtualClock() ?? undefined;
  // Fresh transport per test boot: the MODULE-GLOBAL ordered message queues are
  // shared with the live preview (same `sandbox-area-1->player-1` channel ids). The
  // preview ran on the REAL clock, so its still-queued messages have readyTimes ≈
  // 1.78e12 that can NEVER be ready under THIS boot's virtual clock (now ≈ 0) — and
  // because the queue is sequence-ordered, those un-ready messages head-of-line-
  // BLOCK this client's own join/attach delivery forever (the client never gets its
  // area systems → blank canvas + no input). Dropping them gives this boot clean
  // channels. (resetVirtualClock above already dropped the preview's frozen loop
  // timers, so it can't refill the queue mid-tick.)
  resetOrderedMessageQueues();

  // The game's systems, compiled in the spec's module universe (`shared`), mounted as the one sandbox topology for
  // `n` clients — client i drawing on surfaces[i] (index 0 reused if short) — pumping this boot's own clock until
  // every client has authenticated and joined. The playground scopes each client's key listeners as it is created.
  const { clientSystems, serverSystems } = loadSystemsAndExports(virtualFiles, shared);
  const mounting = mountPlayground({
    name: 'sandbox-test',
    target: document.createElement('div'),
    definition: sandboxPlaygroundDefinition({
      clientSystems,
      serverSystems,
      surfaces: Array.from({ length: n }, (_u, i) => {
        const surface = surfaces[i] ?? surfaces[0];
        return { canvas: surface.canvas, container: surface.container };
      }),
      latencyMs,
      assets: getBootAssetSeam(),
      ...(test ? { tests: [test] } : {}),
    }) as never,
  });
  let mount: Awaited<typeof mounting>;
  try {
    mount = await pumpUntil(mounting, clock);
  } catch (err) {
    // A boot that did not settle within its pump still holds the page's mount: dispose it when it lands.
    void mounting.then((late) => disposeSandboxMount(late), () => undefined);
    throw err;
  }
  liveMount = mount;
  const environment = mount.environment as unknown as SandboxEnvironment;
  // The CanvasProbe asset host reads THIS boot's manager (see getLastBootAssetManager).
  setLastBootAssetManager(mount.environment?.assetManager ?? null);
  applySandboxLatency(environment, latencyMs);
  // Route the input driver's per-step client index to each client's input token.
  setTestClientTokens(Array.from({ length: n }, (_u, i) => inputTokenOf(environment.clients![sandboxClientId(i)] as never)));

  // One tick = advance one fixed step, firing the real client+server game loops +
  // any due transport (latency) timers, deterministically under the virtual clock.
  const tick = async (deltaSeconds: number): Promise<void> => {
    await advance(deltaSeconds * 1000, clock);
  };

  const destroy = async (): Promise<void> => {
    setTestClientTokens([]);
    if (liveMount === mount) liveMount = null;
    await disposeSandboxMount(mount);
  };
  // The test runs on the mount's runner; the boot ends with it, as the executor's own teardown ended it before.
  const runTest: PlaygroundBootedGame['runTest'] = (onProgress) => runSandboxMountTest(mount, destroy, onProgress);

  return {
    harness: {
      // runSandboxTest awaits this; one tick = advance one fixed step, firing the
      // real client+server game loops + any due transport timers, deterministically.
      tick,
      // A step's clientIndex selects the client keys route to — the same focus the input driver sets.
      setFocusedClientIndex: (i: number) => { focusTestClient(i); },
    } as unknown as BootedGame['harness'],
    clientApi: sandboxClientApi(environment, 0),
    clientApis: Array.from({ length: n }, (_u, i) => sandboxClientApi(environment, i)),
    serverApi: sandboxServerApi(environment),
    canvas: surfaces[0]?.canvas ?? null,
    canvases: Array.from({ length: n }, (_u, i) => surfaces[i]?.canvas ?? null),
    clientIds: Array.from({ length: n }, (_u, i) => sandboxClientId(i)),
    disconnect: async (i: number) => {
      // env.removeClient drives area.handleClientLeave → onClientLeft, as the node runner's disconnect does. A leave
      // that throws is logged, not raised — what both runners did before they booted through the playground.
      const env = mount.environment as unknown as { removeClient: (client: unknown) => Promise<void> };
      // From here on a press for this client reaches no client.
      forgetTestClient(i);
      try {
        await pumpUntil(env.removeClient(environment.clients![sandboxClientId(i)]), clock);
      } catch (e) {
        console.warn('Error disconnecting client', e);
      }
    },
    destroy,
    ...(test ? { runTest } : {}),
  };
}
